import React, { useState, useRef, useMemo } from "react";
import {
  Button,
  Grid2,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Snackbar,
  Alert,
  IconButton,
  Container,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetDeviceQuery,
  useGetDeviceMessageHistoryQuery,
  usePowerOnDeviceMutation,
  usePowerOffDeviceMutation,
  useRequestDeviceStatusMutation,
  useModifyDeviceMutation,
} from "../../redux/slices/djangoApi";
import usePowerCycle from "../../hooks/usePowerCycle";
import useFirmwareUpdate from "../../hooks/useFirmwareUpdate";
import DeviceMap from "./DeviceMap";
import ConnectionStatusCard from "./ConnectionStatusCard";
import PowerStatusCard from "./PowerStatusCard";
import PowerControlCard from "./PowerControlCard";
import FirmwareCard from "./FirmwareCard";
import LastMessageCard from "./LastMessageCard";
import { Settings, ArrowBack, EditLocation } from "@mui/icons-material";

const Device = () => {
  const { serialNumber } = useParams();
  const navigate = useNavigate();

  // WebSocket pushes live status updates — no polling needed
  const { data: device } = useGetDeviceQuery(serialNumber);

  const { phase: rebootPhase, triggerCycle } = usePowerCycle(
    serialNumber,
    device
  );

  const { phase: updatePhase, triggerUpdate } = useFirmwareUpdate(
    serialNumber,
    device
  );

  // Fetch stored message history from the API
  const { data: historyData } = useGetDeviceMessageHistoryQuery(serialNumber);

  // Prepend live WebSocket messages that arrive after the API fetch
  const liveMessagesRef = useRef([]);
  const lastMessage = device?.last_message;
  const messages = useMemo(() => {
    // Normalize API history to match the WebSocket message shape
    const history = (historyData || []).map((msg) => ({
      payload: msg.decoded_payload,
      timestamp: msg.timestamp,
      topic: msg.topic,
    }));

    if (!lastMessage) return history;

    const live = liveMessagesRef.current;
    // Only add if it's a new message (different timestamp)
    if (!live.length || live[0].timestamp !== lastMessage.timestamp) {
      // Deduplicate against API history
      const historyTimestamps = new Set(history.map((m) => m.timestamp));
      if (!historyTimestamps.has(lastMessage.timestamp)) {
        liveMessagesRef.current = [lastMessage, ...live].slice(0, 50);
      }
    }

    // Merge: live messages first, then API history, capped at 50
    return [...liveMessagesRef.current, ...history].slice(0, 50);
  }, [lastMessage, historyData]);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");

  const [powerOnDevice] = usePowerOnDeviceMutation();
  const [powerOffDevice] = usePowerOffDeviceMutation();
  const [requestStatus, { isLoading: isRefreshingStatus }] =
    useRequestDeviceStatusMutation();
  const [modifyDevice] = useModifyDeviceMutation();

  const [editingLocation, setEditingLocation] = useState(false);
  const [editLat, setEditLat] = useState("");
  const [editLng, setEditLng] = useState("");

  const showSnack = (message, severity = "success") => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleReboot = async (duration) => {
    try {
      await triggerCycle(duration);
    } catch (err) {
      showSnack("Failed to power cycle device", "error");
    }
  };

  const handleFirmwareUpdate = async () => {
    try {
      await triggerUpdate();
      showSnack("Firmware update initiated");
    } catch (err) {
      showSnack("Failed to update firmware", "error");
    }
  };

  const handlePowerOn = async () => {
    try {
      await powerOnDevice({ sn: serialNumber }).unwrap();
      showSnack("Power on initiated");
    } catch (err) {
      showSnack("Failed to power on device", "error");
    }
  };

  const handlePowerOff = async () => {
    try {
      await powerOffDevice({ sn: serialNumber }).unwrap();
      showSnack("Power off initiated");
    } catch (err) {
      showSnack("Failed to power off device", "error");
    }
  };

  const handleRequestStatus = async () => {
    try {
      await requestStatus({ sn: serialNumber }).unwrap();
      showSnack("Status request sent");
    } catch (err) {
      showSnack("Failed to request status", "error");
    }
  };

  const startEditLocation = () => {
    setEditLat(device?.lat || "");
    setEditLng(device?.lng || "");
    setEditingLocation(true);
  };

  const handleSaveLocation = async () => {
    try {
      await modifyDevice({
        sn: serialNumber,
        data: { lat: editLat, lng: editLng },
      }).unwrap();
      setEditingLocation(false);
      showSnack("Location updated");
    } catch (err) {
      showSnack("Failed to update location", "error");
    }
  };

  const statusDotHex = device?.status === "ONLINE" ? "#22c55e" : "#ef4444";

  const statusColor = device?.status === "ONLINE" ? "success" : "error";

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>

      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/devices")}
        sx={{ mb: 2 }}
      >
        Back to Dashboard
      </Button>

      {!device ? (
        <Box sx={{ pt: 10, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Header */}
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: statusDotHex,
                flexShrink: 0,
              }}
            />
            <Typography variant="h4" fontWeight="bold">
              {device?.name || device?.serial_number}
            </Typography>
            <Chip
              label={device?.status || "OFFLINE"}
              color={statusColor}
              size="small"
            />
            <IconButton onClick={() => navigate("edit")} size="small">
              <Settings fontSize="small" />
            </IconButton>
          </Box>

          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Typography variant="body2" color="text.secondary">
              SN: {device?.serial_number}
              {device?.location && ` \u2022 ${device.location}`}
            </Typography>
            {editingLocation ? (
              <>
                <TextField
                  size="small"
                  label="Latitude"
                  value={editLat}
                  onChange={(e) => setEditLat(e.target.value)}
                  sx={{ width: 150 }}
                  variant="outlined"
                />
                <TextField
                  size="small"
                  label="Longitude"
                  value={editLng}
                  onChange={(e) => setEditLng(e.target.value)}
                  sx={{ width: 150 }}
                  variant="outlined"
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveLocation}
                >
                  Save
                </Button>
                <Button size="small" onClick={() => setEditingLocation(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {device?.lat && device?.lng && (
                  <Typography variant="body2" color="text.secondary">
                    {`\u2022 [${device.lat}, ${device.lng}]`}
                  </Typography>
                )}
                <Button
                  size="small"
                  startIcon={<EditLocation />}
                  onClick={startEditLocation}
                >
                  Edit
                </Button>
              </>
            )}
          </Box>

          {/* Map + Power Status split */}
          <Grid2 container spacing={3} sx={{ mb: 3 }}>
            <Grid2 size={{ xs: 12, md: 8 }}>
              <DeviceMap
                devices={device}
                height={300}
                editable={editingLocation}
                onLocationSelect={(lat, lng) => {
                  setEditLat(lat);
                  setEditLng(lng);
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <PowerStatusCard
                device={device}
                onRefresh={handleRequestStatus}
                isRefreshing={isRefreshingStatus}
                onReboot={handleReboot}
                rebootPhase={rebootPhase}
              />
            </Grid2>
          </Grid2>

          {/* Cards grid */}
          <Grid2 container spacing={3} sx={{ mb: 3 }}>
            {/* Connection Status */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <ConnectionStatusCard device={device} />
            </Grid2>

            {/* Power Control + Firmware */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Box display="flex" flexDirection="column" gap={3}>
                <PowerControlCard
                  device={device}
                  onPowerOn={handlePowerOn}
                  onPowerOff={handlePowerOff}
                />
                <FirmwareCard
                  device={device}
                  onUpdate={handleFirmwareUpdate}
                  updatePhase={updatePhase}
                />
              </Box>
            </Grid2>
          </Grid2>

          {/* MQTT Messages — full width */}
          <LastMessageCard messages={messages} />
        </>
      )}
    </Container>
  );
};

export default Device;
